<template>
<div :class="['minr-field', classes]">
	<label :class="['minr-label', {'sr-only': isHiddenLabel}]" :for="name">
		{{ labelText }}
		<span v-if="isRequired">(Required)</span>
	</label>
	<HelpText v-if="helpText.text" :text="helpText.text" :classes="helpText.classes" />

	<input
		:ref="name"
		class="form-control minr-input"
		:class="{'has-error' : errors.length}"
		:id="name"
		:value="value"
		:name="name"
		:placeholder="placeholder"
		:type="text"
		:required="isRequired">

	<Button class="primary m-t-1 icon-last" text="Search" button-type="button" @btnClick="search" :is-loading="resultsLoading">
		<template #icon>
			<search-icon classes="minr-icon_sml" />
		</template>
	</Button>
	<template v-if="errors.length">
		<fieldError :errors="errors" />
	</template>
	<template v-if="!isCompanySelected">
		<ul class="results">
			<li v-for="(item, key) in searchResults" :key="key">
				<Heading :level="2" class="minr-h6 text-bold" :text="item.CompanyName" />
				<span>{{ item.AddressLine1 }}, {{ item.PostalCode }}, {{ item.Locality }}</span>
				<span>Company number: {{ item.CompanyNumber }}  </span>
				<Button @click="companySelected(item)" class="primary-hollow" text="Select" />
			</li>
		</ul>
	</template>
	<template v-else>
		<UserMessage class="m-t-1" message-type="success" :message-text="`You have selected ${searchResults[0]?.CompanyName || 'a company'}.`" />
	</template>
</div>
</template>

<script>
import companiesHouseClient from '@/services/companiesHouseClient';
import SearchIcon from '@/components/Icons/SearchIcon.vue';

export default {
	name: 'CompaniesHouseSearch',
	components: {
		SearchIcon
	},
	props: {
		name: {
			type: String,
			required: true
		},
		labelText: {
			type: String,
			required: true
		},
		isRequired: {
			type: Boolean,
			default: true
		},
		isHiddenLabel: {
			type: Boolean,
			default: false
		},
		classes: {
			type: String,
			default: ''
		},
		requiredError: {
			type: String,
			default: 'This field is required'
		},
		invalidError: {
			type: String,
			default: ''
		},
		value: {
			type: [String, Number],
			default: ''
		},
		helpText: {
			type: Object,
			default () {
				return { };
			}
		},
		validationRules: {
			type: Array,
			default () {
				return [];
			}
		},
		placeholder: {
			type: String,
			default: ''
		},
		isDisabled: {
			type: Boolean,
			default: false
		}
	},
	emits: ['companySelected'],
	data () {
		return {
			errors: [],
			searchResults: [],
			resultsLoading: false,
			isCompanySelected: false
		};
	},
	mounted () {
		// Here we only consider the company to be selected if the local storage item is set to true
		this.isCompanySelected = window.localStorage.getItem('companySelected') === 'true';
	},
	methods: {
		companiesHouseNum () {
			const pattern = /([a-z]{2})?[0-9]{6,8}/;
			return (pattern.test(String(this.value).toLowerCase()) && this.value.length === 8) || 'Please enter a valid Companies House Number';
		},
		search () {
			this.validate();
			if (this.errors.length === 0) {
				this.resultsLoading = true;
				this.searchResults = [];
				this.isCompanySelected = false;
				window.localStorage.setItem('companySelected', false);

				companiesHouseClient.get(`/CompanySearch/GetCompanyByNumber/${this.value}`)
					.then((res) => {
						if (res.status === 200 && res.data !== null) {
							this.searchResults.push({
								CompanyName: res.data.CompanyName,
								AddressLine1: res.data.RegisteredOfficeAddress.AddressLine1,
								AddressLine2: res.data.RegisteredOfficeAddress.AddressLine2,
								PostalCode: res.data.RegisteredOfficeAddress.PostalCode,
								Locality: res.data.RegisteredOfficeAddress.Locality,
								CompanyNumber: res.data.CompanyNumber,
								SicCode: res.data.SicCodes !== null ? res.data.SicCodes.join('-') : '',
								DateOfCreation: res.data.DateOfCreation
							});
						} else {
							this.errors.push('This company number does not exist in Companies House Data, please enter the correct value.');
						}
					}).catch(() => {
						this.errors.push("We're having trouble fetching Companies House Data, please try again later.");
					}).finally(() => {
						this.resultsLoading = false;
					});
			}
		},
		companySelected (selectedItem) {
			this.isCompanySelected = true;
			this.$emit('companySelected', selectedItem);

			window.localStorage.setItem('companySelected', true);
		},
		validate () {
			this.errors = [];
			const elem = this.$refs[this.name];
			elem.setCustomValidity('');
			if (this.isRequired || this.value) {
				if (this.isRequired && (!this.value || this.value.trim() === '')) {
					this.errors.push(this.requiredError);
					elem.setCustomValidity(this.requiredError);
				} else {
					elem.setCustomValidity('');
				}
			}
			const isValid = this.companiesHouseNum();
			if (isValid === true) {
				elem.setCustomValidity('');
			} else {
				elem.setCustomValidity(isValid);
				this.errors.push(isValid);
			}
		}
	}
};

</script>

<style lang="scss" scoped>
@import '../../../assets/scss/components/companiesHouse.scss';
</style>
